import { DeleteOutlined, DownOutlined, PlusOutlined } from "@ant-design/icons";
import type { ProColumns, ProFormInstance } from "@ant-design/pro-components";
import {
  ModalForm,
  ProForm,
  ProFormDatePicker,
  ProFormDateTimePicker,
  ProFormDigit,
  ProFormSelect,
  ProFormSwitch,
  ProFormText,
  ProFormTextArea,
} from "@ant-design/pro-components";
import { useIntl, useModel } from "@umijs/max";
import {
  Button,
  Col,
  Collapse,
  ColorPicker,
  DatePicker,
  Divider,
  Dropdown,
  Form,
  message,
  Row,
  Space,
  Tabs,
  Checkbox,
} from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Ckeditor5 from "./components/Ckeditor5";
import CustomImageUpload from "./components/CustomImageUpload";
import CustomImageUploadToMedia from "./components/CustomImageUploadToMedia";
export type FormItemType = ProColumns & {
  render?: (value: any, currentValues: any) => React.ReactNode; // 外部传入的表单组件

  rules?: any[];
  span?: number; // 自定义占用列数，默认 12
  type?:
    | "text"
    | "select"
    | "textarea"
    | "switch"
    | "date"
    | "datetime"
    | "image"
    | "upload"
    | "input"
    | "number"
    | "colorPicker"
    | "ckeditor5"
    | "component"
    | "checkboxGroup";

  componentProps?: any;

  options?: { label: string; value: any }[];

  // 用于component类型，定义子表单项
  component?: FormItemType[];

  // 条件显示配置
  dependencies?: string[]; // 依赖的字段名数组
  condition?: (values: any) => boolean; // 条件函数，返回true时显示该字段
};

export type FormValueType = {
  [key: string]: any;
} & Partial<API.RuleListItem>;

export type UpdateFormProps = {
  modalConfig: {
    title?: string;
    key: string;
    show: boolean;
    width?: number;
  };
  onOk?: () => void;
  onSubmit?: (values: any) => Promise<void>;
  rowData?: Partial<API.RuleListItem>;
  formConfig?: FormItemType[];

  updateApi?: (params: { data: any }) => Promise<any>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  header?: any;
  readonly?: any;
  layout?: any;
  locales?: any;
};

const CustomEditForm: React.FC<UpdateFormProps> = (props) => {
  const formRef = useRef<ProFormInstance>(null);
  const {
    onOk,
    onSubmit,
    rowData = {},
    formConfig = [],
    modalConfig,
    header,
    readonly,
    layout,
    locales,
    onVisibleChange,
  } = props;
  const { initialState } = useModel("@@initialState");
  const { currentUser }: any = initialState || {};
  const [langRawData, setLangRawData]: any = useState({});
  const intl = useIntl();
  const [messageApi, contextHolder] = message.useMessage();
  const renderFormItem = (item: any, currentValues: any, name?: any) => {
    const options = item.request
      ? useDynamicOptions(item.request)
      : item.options;
    // width一般是表格列宽度，到这里直接传入表单会影响表单项宽度，暂时不用，后面表单属性看看有单独放在表单对象中设置宽度之类的
    const {
      dataIndex,
      title,
      width,
      renderForm,
      dependencies,
      condition,
      ...rest
    } = item;
    const commonProps: any = {
      name: name || dataIndex,
      label: title || dataIndex,
      ...rest,
    };
    if (item?.onChange) {
      commonProps.onChange = item?.onChange;
    }
    if (item?.renderForm) {
      commonProps.render = item?.renderForm;
    }

    // 如果配置了依赖和条件，使用 shouldUpdate 包装
    if (dependencies && condition) {
      return (
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            // 获取所有依赖字段的值
            const depValues: any = {};
            dependencies.forEach((dep) => {
              depValues[dep] = getFieldValue(dep);
            });

            // 根据条件函数判断是否显示
            if (!condition(depValues)) {
              return null;
            }

            // 渲染实际的表单项
            return renderActualFormItem(
              item,
              currentValues,
              name,
              options,
              commonProps
            );
          }}
        </Form.Item>
      );
    }

    return renderActualFormItem(
      item,
      currentValues,
      name,
      options,
      commonProps
    );
  };

  const renderActualFormItem = (
    item: any,
    currentValues: any,
    name?: any,
    options?: any,
    commonProps?: any
  ) => {
    if (!commonProps) {
      const {
        dataIndex,
        title,
        width,
        renderForm,
        dependencies,
        condition,
        ...rest
      } = item;
      commonProps = {
        name: name || dataIndex,
        label: title || dataIndex,
        ...rest,
      };
      if (item?.onChange) {
        commonProps.onChange = item?.onChange;
      }
      if (item?.renderForm) {
        commonProps.render = item?.renderForm;
      }
    }
    if (!options) {
      options = item.request ? useDynamicOptions(item.request) : item.options;
    }
    switch (item.type) {
      case "select":
        return (
          <ProFormSelect {...commonProps} options={options} mode={item.mode} />
        );
      case "checkboxGroup":
        return (
          <ProForm.Item {...commonProps}>
            <Checkbox.Group
              options={options}
              disabled={
                modalConfig.key === "Details" ||
                (modalConfig.key === "Edit" && commonProps.disabledEdit)
              }
              {...item.componentProps}
            />
          </ProForm.Item>
        );
      case "textarea":
        return <ProFormTextArea {...commonProps} />;
      case "switch":
        return <ProFormSwitch {...commonProps} />;
      case "date":
        return <ProFormDatePicker {...commonProps} />;
      case "datetime":
        // 如果 fieldProps.type === 'range'，使用 DatePicker.RangePicker
        if (item.fieldProps?.type === "range") {
          const { type, ...restFieldProps } = item.fieldProps || {};
          return (
            <ProForm.Item {...commonProps}>
              <DatePicker.RangePicker
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                style={{ width: "100%" }}
                {...item.componentProps}
                {...restFieldProps}
              />
            </ProForm.Item>
          );
        }
        return <ProFormDateTimePicker {...commonProps} />;
      case "input":
        return <ProFormText {...commonProps} />;
      case "number":
        return <ProFormDigit {...commonProps} />;
      case "ckeditor5":
        return (
          <ProForm.Item {...commonProps}>
            <Ckeditor5
              {...item.componentProps}
              disabled={modalConfig.key === "Details"}
            />
          </ProForm.Item>
        );
      case "image":
        return (
          <ProForm.Item {...commonProps}>
            <CustomImageUpload
              {...item.componentProps}
              optType={modalConfig.key}
              disabled={modalConfig.key === "Details"}
            />
          </ProForm.Item>
        );
      case "imageToMedia":
        return (
          <ProForm.Item {...commonProps}>
            <CustomImageUploadToMedia
              changeImageBtnText={commonProps?.changeImageBtnText}
              uploadUrl={commonProps?.uploadResourceUrl}
              rawdata={commonProps}
              optType={modalConfig.key}
            />
          </ProForm.Item>
        );
      case "colorPicker":
        return (
          <ProForm.Item {...commonProps}>
            <ColorPicker
              format="hex"
              showText
              disabled={
                modalConfig.key === "Details" ||
                (modalConfig.key === "Edit" && commonProps.disabledEdit)
              }
              styles={{
                popupOverlayInner: {
                  width: 468 + 24,
                },
              }}
              presets={[
                {
                  label: "Recommended",
                  colors: [
                    "#000000",
                    "#000000E0",
                    "#000000A6",
                    "#00000073",
                    "#00000040",
                    "#00000026",
                    "#0000001A",
                    "#00000012",
                    "#0000000A",
                    "#00000005",
                    "#F5222D",
                    "#FA8C16",
                    "#FADB14",
                    "#8BBB11",
                    "#52C41A",
                    "#13A8A8",
                    "#1677FF",
                    "#2F54EB",
                    "#722ED1",
                    "#EB2F96",
                    "#F5222D4D",
                    "#FA8C164D",
                    "#FADB144D",
                    "#8BBB114D",
                    "#52C41A4D",
                    "#13A8A84D",
                    "#1677FF4D",
                    "#2F54EB4D",
                    "#722ED14D",
                    "#EB2F964D",
                  ],
                },
                // {
                //   label: 'Recent',
                //   colors: ['#F5222D4D', '#FA8C164D', '#FADB144D', '#8BBB114D', '#52C41A4D', '#13A8A84D',],
                // },
              ]}
              panelRender={(_, { components: { Picker, Presets } }) => (
                <div
                  className="custom-panel"
                  style={{
                    display: "flex",
                    width: 468,
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                    }}
                  >
                    <Presets />
                  </div>
                  <Divider
                    type="vertical"
                    style={{
                      height: "auto",
                    }}
                  />
                  <div
                    style={{
                      width: 234,
                    }}
                  >
                    <Picker />
                  </div>
                </div>
              )}
            />
          </ProForm.Item>
        );
      case "uniqueView":
        // 处理uniqueView类型，使用renderRowWithCols函数渲染children
        return (
          <ProForm.Item label={item?.title || item?.dataIndex}>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => {
                const fieldValue = getFieldValue(item?.dataIndex);
                // 如果字段值不存在（undefined 或 null），显示添加按钮
                if (!fieldValue) {
                  if (modalConfig.key === "Details") return null;
                  return (
                    <Button
                      block
                      type="dashed"
                      onClick={() => setFieldsValue({ [item?.dataIndex]: {} })}
                      icon={<PlusOutlined />}
                    >
                      添加 {item?.title || item?.dataIndex}
                    </Button>
                  );
                }
                // 有值就渲染表单
                return renderRowWithCols(item.children, currentValues, [
                  item?.dataIndex,
                ]);
              }}
            </Form.Item>
          </ProForm.Item>
        );
      case "collapse":
        // 处理数组类型的component，使用Collapse组件渲染
        return renderComponentItem(item, currentValues, name);
      case "component":
        return (
          <ProForm.Item {...commonProps}>
            {React.createElement(commonProps.component, {
              ...item.componentProps,
              optType: modalConfig.key,
              disabled: modalConfig.key === "Details",

              // value: form.getFieldValue(formItem.dataIndex),
              // onChange: formItem.componentOnChange,
            })}
          </ProForm.Item>
        );
      default:
        break;
    }
    if (commonProps.render) {
      return (
        <ProForm.Item {...commonProps}>
          {commonProps.render(currentValues?.[item.dataIndex], currentValues)}
        </ProForm.Item>
      );
    }
    return <ProFormText {...commonProps} />;
  };

  // 渲染component类型的表单项
  // 将Row包装成一个可复用的函数
  const renderRowWithCols = (
    children: any[],
    currentValues: any,
    namePrefix?: any
  ) => {
    // 先处理 modalConfig 相关的逻辑
    const processedChildren = children?.map((childItem: any) => {
      const processedItem = { ...childItem };

      if (modalConfig.key === "Add") {
        if (processedItem?.disabledInAdd)
          processedItem.disabled = processedItem?.disabledInAdd;
        if (processedItem?.hideInAdd)
          processedItem.hideInForm = processedItem?.hideInAdd;
      }
      if (modalConfig.key === "Edit") {
        if (processedItem?.disabledInEdit)
          processedItem.disabled = processedItem?.disabledInEdit;
        if (processedItem?.hideInEdit)
          processedItem.hideInForm = processedItem?.hideInEdit;
      }

      // 处理formChildren中的子项
      if (processedItem?.formChildren) {
        processedItem.formChildren = processedItem.formChildren?.map(
          (jtem: any) => {
            const processedJtem = { ...jtem };

            if (modalConfig.key === "Add") {
              if (processedJtem?.disabledInAdd)
                processedJtem.disabled = processedJtem?.disabledInAdd;
              if (processedJtem?.hideInAdd)
                processedJtem.hideInForm = processedJtem?.hideInAdd;
            }
            if (modalConfig.key === "Edit") {
              if (processedJtem?.disabledInEdit)
                processedJtem.disabled = processedJtem?.disabledInEdit;
              if (processedJtem?.hideInEdit)
                processedJtem.hideInForm = processedJtem?.hideInEdit;
            }

            return processedJtem;
          }
        );
      }

      return processedItem;
    });

    return (
      <Row gutter={16} style={{ background: "#665b5b05", borderRadius: "8px" }}>
        {processedChildren?.map((childItem: any) => {
          // 如果配置了条件显示，使用 shouldUpdate 包装整个 Col
          if (childItem.dependencies && childItem.condition) {
            return (
              <Form.Item key={childItem.dataIndex} noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const depValues: any = {};
                  childItem.dependencies.forEach((dep: string) => {
                    depValues[dep] = getFieldValue(dep);
                  });

                  if (!childItem.condition(depValues)) {
                    return null;
                  }

                  // 处理formChildren的情况
                  if (childItem?.formChildren) {
                    childItem.formChildren.map(
                      (item: any) => (item.span = item.span || 24)
                    );
                    return (
                      <Col
                        key={childItem.dataIndex}
                        span={childItem.span || 12}
                      >
                        <ProForm.Item
                          {...childItem}
                          label={childItem?.title || childItem?.dataIndex}
                        >
                          <div style={{ width: "100%", paddingTop: "30px" }}>
                            {renderRowWithCols(
                              childItem.formChildren,
                              currentValues,
                              namePrefix
                                ? [...namePrefix, childItem.dataIndex]
                                : [childItem.dataIndex]
                            )}
                          </div>
                        </ProForm.Item>
                      </Col>
                    );
                  }

                  return (
                    !childItem.hideInForm && (
                      <Col span={childItem.span || 12}>
                        {renderFormItem(
                          childItem,
                          currentValues,
                          namePrefix
                            ? [...namePrefix, childItem.dataIndex]
                            : childItem.dataIndex
                        )}
                      </Col>
                    )
                  );
                }}
              </Form.Item>
            );
          }

          // 处理formChildren的情况
          if (childItem?.formChildren) {
            childItem.formChildren.map(
              (item: any) => (item.span = item.span || 24)
            );
            return (
              <Col key={childItem.dataIndex} span={childItem.span || 12}>
                <ProForm.Item
                  {...childItem}
                  label={childItem?.title || childItem?.dataIndex}
                >
                  <div style={{ width: "100%", paddingTop: "30px" }}>
                    {renderRowWithCols(
                      childItem.formChildren,
                      currentValues,
                      namePrefix
                        ? [...namePrefix, childItem.dataIndex]
                        : [childItem.dataIndex]
                    )}
                  </div>
                </ProForm.Item>
              </Col>
            );
          }
          return (
            !childItem.hideInForm && (
              <Col key={childItem.dataIndex} span={childItem.span || 12}>
                {renderFormItem(
                  childItem,
                  currentValues,
                  namePrefix
                    ? [...namePrefix, childItem.dataIndex]
                    : childItem.dataIndex
                )}
              </Col>
            )
          );
        })}
      </Row>
    );
  };

  const renderComponentItem = (
    item: any,
    currentValues: any,
    name?: string
  ) => {
    const { dataIndex, title, component = [], ...rest } = item;
    // 获取当前值，如果不存在则初始化为空数组
    const currentValue = (currentValues?.[dataIndex] || []) as any[];
    return (
      <ProForm.Item label={title || dataIndex} name={dataIndex} {...rest}>
        <Form.List name={name || item.dataIndex}>
          {(fields, { add, remove }) => (
            <>
              {!!fields?.length && (
                <Collapse
                  style={{ marginBottom: 10 }}
                  items={fields.map(({ key, name, ...restField }, index) => ({
                    key: key.toString(),
                    label: `${item.title || item.dataIndex} - ${index}`,
                    extra: modalConfig.key !== "Details" && (
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)} // 使用name而不是index
                        danger
                      />
                    ),
                    children: renderRowWithCols(
                      item.children,
                      currentValue?.[index] || {},
                      [name]
                    ),
                  }))}
                />
              )}
              {modalConfig.key !== "Details" && (
                <Button type="dashed" onClick={() => add()} block>
                  添加
                </Button>
              )}
            </>
          )}
        </Form.List>
      </ProForm.Item>
    );
  };
  const [addlangs, setAddlangs]: any = useState([]);
  const [selectLang, setSelectLang]: any = useState(
    currentUser?.allLocales[0]?.code
  );
  const langTabItems: any = useMemo(() => {
    return currentUser?.allLocales?.map((item: any, index: any) => ({
      label: item?.name,
      key: item?.code,
      disabled: addlangs?.length < 1 && modalConfig.key === "Add" && index > 0,
    }));
  }, [currentUser?.allLocales, modalConfig.key, formConfig, langRawData]);

  const onLangChange = async (key: any) => {
    setSelectLang(key);
    let resData: any;
    if (locales?.request) {
      resData = await locales?.request({
        ...langRawData[currentUser?.allLocales[0]?.code],
        formSelectLocale: key,
      });
      if (resData) setLangRawData({ ...langRawData, [key]: resData });
    }
    // setTimeout(() => {
    formRef.current?.resetFields();
    formRef.current?.setFieldsValue(resData || langRawData[key]);
    // });
  };
  useEffect(() => {
    if (locales) {
      setLangRawData({ [currentUser?.allLocales[0]?.code]: rowData });
      formRef.current?.setFieldsValue(rowData || {});
    }
  }, []);

  return (
    <>
      {contextHolder}

      <ModalForm
        formRef={formRef}
        key={JSON.stringify(rowData)}
        // key={`${modalConfig.key}-${selectLang}-${
        //   langRawData[selectLang]?.documentId || "new"
        // }`}
        initialValues={locales ? langRawData[selectLang] || {} : rowData}
        // initialValues={rowData}
        width={modalConfig.width || "1300px"}
        layout={layout || "horizontal"}
        submitter={{
          searchConfig: {
            submitText: intl.formatMessage({
              id: "pages.searchTable.submit",
              defaultMessage: "Submit",
            }),
            resetText: intl.formatMessage({
              id: "pages.searchTable.cancel",
              defaultMessage: "Cancel",
            }),
          },
          render: (_, dom) =>
            readonly || modalConfig.key === "Details" ? null : dom,
        }}
        readonly={readonly || modalConfig.key === "Details"}
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              paddingRight: "40px",
            }}
          >
            <div>
              {modalConfig.title ||
                intl.formatMessage(
                  modalConfig.key === "add"
                    ? {
                        id: "pages.searchTable.createForm.add",
                        defaultMessage: "New",
                      }
                    : modalConfig.key === "edit"
                    ? {
                        id: "pages.searchTable.createForm.edit",
                        defaultMessage: "Edit",
                      }
                    : {
                        id: "pages.searchTable.createForm.detail",
                        defaultMessage: "Detail",
                      }
                )}
            </div>
            {locales && (
              <Dropdown
                overlayStyle={{ fontSize: "12px" }}
                menu={{
                  items: langTabItems?.filter(
                    (langItem: any) => langItem?.key !== selectLang
                  ),
                  onClick: ({ key }) => {
                    const { documentId, createdAt, updatedAt, ...rest }: any =
                      langRawData[key];
                    if (langRawData[key])
                      formRef.current?.setFieldsValue({ ...rest });
                  },
                }}
              >
                <a onClick={(e) => e.preventDefault()}>
                  <Space>
                    回填其他语言数据
                    <DownOutlined />
                  </Space>
                </a>
              </Dropdown>
            )}
          </div>
        }
        onOpenChange={onVisibleChange}
        open={modalConfig.show}
        onCancel={() => (modalConfig.show = false)}
        onFinish={async (values) => {
          const payload = {
            ...values,
          };
          if (locales) {
            payload.formSelectLocale = selectLang;
            payload.formAlllocales = currentUser?.allLocales;
            payload.documentId =
              langRawData[currentUser?.allLocales[0]?.code]?.documentId ||
              rowData?.documentId;
          } else if (rowData?.documentId)
            payload.documentId = rowData?.documentId;
          const resSubmit: any = await onSubmit?.(payload);
          if (resSubmit?.addlangs) setAddlangs(resSubmit?.addlangs);
          if (resSubmit?.createData)
            setLangRawData({
              ...langRawData,
              [selectLang]: resSubmit?.createData,
            });
          // ProForm 期望返回 true/false 控制是否关闭弹窗
          return false;
        }}
      >
        {header}
        {locales ? (
          <Tabs
            activeKey={selectLang}
            items={langTabItems}
            onChange={onLangChange}
          />
        ) : (
          ""
        )}
        {renderRowWithCols(
          formConfig,
          locales ? langRawData[selectLang] : rowData
        )}
        {modalConfig.key === "Add" && addlangs?.length ? (
          <div style={{ color: "red" }}>
            请继续完成多语言配置：
            {currentUser?.allLocales
              ?.filter((item: any) => !addlangs.includes(item?.code))
              ?.map((item: any) => item?.name)
              ?.join(",")}
          </div>
        ) : (
          ""
        )}
      </ModalForm>
    </>
  );
};
const useDynamicOptions = (optionsProp: any) => {
  const [options, setOptions] = useState([]);
  // 只对需要选项的类型处理
  useEffect(() => {
    (async () => {
      const data = await optionsProp();
      setOptions(data || []);
    })();
    // 如果 optionsProp 未定义或 null，什么都不做
  }, []);

  return options;
};
export default CustomEditForm;
